import { useCallback } from 'react';
import { useFormContext, useFormInstance } from '@adv-frontend/form-kit';
import { updateOrderProductQuantity } from '@/entities/order';
import { InvalidDeliveryFloorModal } from '@/features/InvalidDeliveryFloorModal';
import { InvalidEmailModal } from '@/features/InvalidEmailModal';
import { InvalidPhoneModal } from '@/features/InvalidPhoneModal';
import { LoyaltyApplyError } from '@/features/LoyaltyApplyError';
import { LoyaltyPromoModal } from '@/features/LoyaltyPromoModal';
import { LoyaltyRegistrationModal } from '@/features/LoyaltyRegistrationModal';
import { MinimumOrderAmountNotMetModal } from '@/features/MinimumOrderAmountNotMetModal';
import { ORDER_FORM_ID, orderForm, reinitializeOrderForm } from '@/features/OrderForm';
import { OrderSubmissionError } from '@/features/OrderSubmissionError';
import { OutdatedDeliveryPricesModal } from '@/features/OutdatedDeliveryPricesModal';
import { OutdatedItemPricesModal } from '@/features/OutdatedItemPricesModal';
import { OutdatedLoyaltyConditions } from '@/features/OutdatedLoyaltyConditions';
import { OutdatedStockModal } from '@/features/OutdatedStockModal';
import { OzonRegistrationModal } from '@/features/OzonRegistrationModal';
import { PayBoxModal } from '@/features/PayBoxModal';
import { PromoCodeUsageLimitExceededModal } from '@/features/PromoCodeUsageLimitExceededModal';
import { StockChangedModal } from '@/features/StockChangedModal';
import { UpdateOrderFailureModal } from '@/features/UpdateOrderFailureModal';
import { type OrderDto } from '@/shared/api/order';
import {
    type UseLoyaltyRegisterModalControllerPayload,
    useMessageToast,
    type UseModalControllerPayload,
    type UseOutdatedStockModalControllerPayload,
} from '@/shared/lib/hooks';

interface ModalsProps {
    order: OrderDto;
    signature: string;
    storeSlug: string;
    payboxUrl: string;
    openRegistrationOzon: VoidFunction;
    modals: {
        outdatedItemPrices: UseModalControllerPayload;
        outdatedDeliveryPrices: UseModalControllerPayload;
        outdatedStock: UseOutdatedStockModalControllerPayload;
        stockChanged: UseModalControllerPayload;
        minimumOrderAmountNotMet: UseModalControllerPayload;
        promoCodeUsageLimitExceeded: UseModalControllerPayload;
        invalidEmail: UseModalControllerPayload;
        invalidPhone: UseModalControllerPayload;
        orderSubmissionError: UseModalControllerPayload;
        payBoxModal: UseModalControllerPayload;
        loyaltyApplyError: UseModalControllerPayload;
        outdatedLoyaltyConditions: UseModalControllerPayload;
        invalidDeliveryFloor: UseModalControllerPayload;
        onOzonRegistrationError: UseModalControllerPayload;
        updateOrderFailure: UseModalControllerPayload;
        loyaltyPromo: UseModalControllerPayload;
        loyaltyRegister: UseLoyaltyRegisterModalControllerPayload;
    };
}

export const Modals = (props: ModalsProps) => {
    const { modals, order, signature, storeSlug, payboxUrl, openRegistrationOzon } = props;
    const formInstance = useFormInstance({ form: orderForm, id: ORDER_FORM_ID });
    const context = useFormContext({ form: orderForm, id: ORDER_FORM_ID });
    const { showToast } = useMessageToast();

    const onUpdateOrder = useCallback(async () => {
        modals.outdatedStock.onClose();

        try {
            await updateOrderProductQuantity({
                items:
                    modals.outdatedStock.insufficientItems?.map((item) => {
                        return {
                            id: item.id,
                            quantity: item.available,
                            delivery_chunk_id: item.delivery_chunk_id || 0,
                        };
                    }) ?? [],
                order_id: order.id,
                signature,
                store_slug: storeSlug,
                reinitialize: (updatedOrder) => reinitializeOrderForm(order, updatedOrder),
            });
        } catch (error) {
            showToast({ title: 'Не удалось обновить заказ' });

            throw new Error('Не удалось обновить заказ');
        }
    }, [modals.outdatedStock, order, showToast, signature, storeSlug]);

    const onSubmit = async () => {
        await formInstance.submit();
        modals.orderSubmissionError.onClose();
    };

    return (
        <>
            <OutdatedItemPricesModal
                {...modals.outdatedItemPrices}
                orderId={order.id}
                storeSlug={storeSlug}
                signature={signature}
            />
            <OutdatedDeliveryPricesModal
                {...modals.outdatedDeliveryPrices}
                orderId={order.id}
                storeSlug={storeSlug}
                signature={signature}
            />
            <OutdatedStockModal
                {...modals.outdatedStock}
                orderItems={order.delivery_chunks.flatMap((chunk) => chunk.items)}
                insufficientItems={modals.outdatedStock.insufficientItems ?? []}
                onUpdateOrder={onUpdateOrder}
            />
            {modals.stockChanged && <StockChangedModal {...modals.stockChanged} />}
            <PayBoxModal {...modals.payBoxModal} payboxUrl={payboxUrl} />
            <MinimumOrderAmountNotMetModal {...modals.minimumOrderAmountNotMet} />
            <PromoCodeUsageLimitExceededModal {...modals.promoCodeUsageLimitExceeded} />
            <InvalidEmailModal {...modals.invalidEmail} />
            <InvalidPhoneModal {...modals.invalidPhone} />
            <OrderSubmissionError {...modals.orderSubmissionError} onSubmit={onSubmit} />
            <OutdatedLoyaltyConditions
                {...modals.outdatedLoyaltyConditions}
                orderId={order.id}
                refresh={context.refetch}
                storeSlug={storeSlug}
                signature={signature}
            />
            <LoyaltyApplyError {...modals.loyaltyApplyError} refresh={context.refetch} />
            <InvalidDeliveryFloorModal {...modals.invalidDeliveryFloor} />
            <OzonRegistrationModal {...modals.onOzonRegistrationError} onSubmit={openRegistrationOzon} />
            <UpdateOrderFailureModal {...modals.updateOrderFailure} />
            <LoyaltyPromoModal {...modals.loyaltyPromo} loyalty={order.loyalty_v2} />
            <LoyaltyRegistrationModal
                {...modals.loyaltyRegister}
                loyalty={order.loyalty_v2}
                userPhone={context.order.client.phone}
            />
        </>
    );
};
